import * as Msal from '@azure/msal-browser';

export default class MsAuthenticationService {
  protected clientId: string;
  protected authority: string;
  protected usePopupFlow: boolean;
  protected msalConfig: Msal.Configuration;
  protected msalObj: Msal.PublicClientApplication;
  protected initialized: boolean;
  protected storeAuthStateInCookie: boolean;

  constructor(clientId: string, authority: string) {
    this.clientId = clientId;
    this.authority = authority;
    // use popup flow for desktop too, as for some users in dev the redirect flow does not work, though in production it works fine with identical codebase.
    this.usePopupFlow = true;
    this.msalConfig = this.getMsalConfig();
    this.msalObj = new Msal.PublicClientApplication(this.msalConfig);
    this.initialized = false;
    this.storeAuthStateInCookie = true;
  }

  protected requestConfig = {
    scopes: ["user.read"],
  };

  // In case the url contains the session or similar we need to cut the url to the original link to be used as redirect URI, otherwise Azure AD will throw an error.
  protected getRedirectUrl(): string {
    if (!window.location || !window.location.origin) {
      console.error('[AuthenticationService] window.location not available');
      return window.location?.href || 'http://localhost';
    }
    
    let redirectUrl = window.location.origin + window.location.pathname;

    if (redirectUrl.endsWith('/')) {
      redirectUrl = redirectUrl.slice(0, -1);
    }
    
    console.log('[AuthenticationService] Redirect URL:', redirectUrl);
    return redirectUrl;
  }

  protected getMsalConfig(): Msal.Configuration {
    const redirectUrl = this.getRedirectUrl();
    return {
      auth: {
        clientId: this.clientId, // This is the ONLY mandatory field that you need to supply.
        authority: this.authority, // Defaults to "https://login.microsoftonline.com/common"
        redirectUri: redirectUrl,
        postLogoutRedirectUri: redirectUrl
      },
      cache: {
        cacheLocation: "localStorage", // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO.
        storeAuthStateInCookie: this.storeAuthStateInCookie, // If you wish to store cache items in cookies as well as browser cache, set this to "true".
      },
      system: {
        loggerOptions: {
          loggerCallback: (level, message, containsPii) => {
            if (containsPii) {
              return;
            }
            switch (level) {
              case Msal.LogLevel.Error:
                this.logError('Azure AD authentication: ', message);
                return;
              case Msal.LogLevel.Info:
                console.info('Azure AD authentication: ', message);
                return;
              case Msal.LogLevel.Verbose:
                console.debug('Azure AD authentication: ', message);
                return;
              case Msal.LogLevel.Warning:
                console.warn('Azure AD authentication: ', message);
                return;
            }
          }
        }
      }
    };
  }

  async initialize(): Promise<void> {
    if (!this.initialized) {
      await this.msalObj.initialize();
      this.initialized = true;
    }
  }

  async login(): Promise<Msal.AuthenticationResult | null> {
    try {
      if (!this.initialized) {
        await this.initialize();
      }

      let accountObj: Msal.AccountInfo | null = null;
      let tokenResponse: Msal.AuthenticationResult | null = null;

      if (!this.usePopupFlow) {
        tokenResponse = await this.msalObj.handleRedirectPromise();
        if (tokenResponse) {
          localStorage.removeItem('azure_login_failed');
          return tokenResponse;
        } else {
          accountObj = this.msalObj.getAllAccounts()[0] || null;
        }
      } else {
        accountObj = this.msalObj.getAllAccounts()[0] || null;
      }

      if (accountObj) {
        try {
          console.warn("[AuthService.login] User has logged in, but no tokens.");
          const silentToken = await this.acquireTokenSilent(accountObj);
          localStorage.removeItem('azure_login_failed');
          return silentToken;
        } catch (e) {
          this.logError("[AuthService.login] acquireTokenSilent failed. Initiating explicit login.", e);
          return await this.loginExplicitly();
        }
      } else {
        this.logError("[AuthService.login] No accountObject. Initiating explicit login.");
        return await this.loginExplicitly();
      }
    } catch (error) {
      this.logError("[AuthService.login] Error: ", error)
      throw error;
    }
  }

  protected async acquireTokenSilent(accountObj: Msal.AccountInfo): Promise<Msal.AuthenticationResult> {
    try {
      return await this.msalObj.acquireTokenSilent({
        account: accountObj,
        scopes: this.requestConfig.scopes
      });
    } catch (silentAcquireError) {
      console.warn("[AuthService.login] Silent token acquisition failed:", silentAcquireError);

      return this.acquireTokenForAccount();
    }
  }

  protected async acquireTokenForAccount(): Promise<Msal.AuthenticationResult> {
    if (this.usePopupFlow) {
      return await this.msalObj.acquireTokenPopup(this.requestConfig);
    } else {
      await this.msalObj.acquireTokenRedirect(this.requestConfig);
      // Redirect never returns, but TypeScript needs a return
      throw new Error("Redirect initiated");
    }
  }

  protected async loginExplicitly(): Promise<Msal.AuthenticationResult> {
    const loginFailedKey = 'azure_login_failed';
    const lastAttemptKey = 'azure_login_last_attempt';
    const now = Date.now();
    const lastAttempt = parseInt(localStorage.getItem(lastAttemptKey) || '0', 10);
    
    // Reset the flag if it's been more than 5 minutes since the last attempt
    if (now - lastAttempt > 5 * 60 * 1000) {
      localStorage.removeItem(loginFailedKey);
    }
    
    if (localStorage.getItem(loginFailedKey) === 'true') {
      throw new Error("Azure could not login user.");
    }
    
    localStorage.setItem(loginFailedKey, "true");
    localStorage.setItem(lastAttemptKey, now.toString());
    
    let response: Msal.AuthenticationResult;
    if (this.usePopupFlow) {
      response = await this.msalObj.loginPopup(this.requestConfig);
    } else {
      await this.msalObj.loginRedirect(this.requestConfig);
      // Redirect never returns, but TypeScript needs a return
      throw new Error("Redirect initiated");
    }
    localStorage.removeItem(loginFailedKey);
    localStorage.removeItem(lastAttemptKey);
    return response;
  }

  protected logError(errMessage: string, error: any = ''): void {
    console.error(errMessage, error);
  }

  async getAccount(): Promise<Msal.AccountInfo | null> {
    if (!this.initialized) {
      await this.initialize();
    }
    const accounts = this.msalObj.getAllAccounts();
    return accounts.length > 0 ? accounts[0] : null;
  }

  async logout(): Promise<void> {
    if (!this.initialized) {
      await this.initialize();
    }
    await this.msalObj.logoutPopup();
  }
}
